import Channel from '@shared/classes/channel';
import CmpUtils from '@cmp';
import DateUtils from './date';
import DeviceConfig from '@devices/config';
import Pages from '@pages';
import ParserUtils from './parser';
import User from '../classes/user';
import { store } from '../store/store';

/**
 * Ad periodicity multipliers in minutes.
 * Used to calculate time offsets between ad impressions.
 */
const AdPeriodicity = {
  hour: 60,
  day: 60 * 24,
  week: 60 * 24 * 7,
} as { [type: string]: number };

/**
 * Utility object containing functions for ad management and processing.
 * Handles ad source generation, macro parsing, impression tracking, and more.
 */
const AdUtils = {
  /**
   * Checks if enough time has passed since the last ad impression.
   * Calculates the required offset based on plan configuration and compares it with the last impression time.
   *
   * @returns {boolean} True if ads can be shown, false if the time offset is too short
   */
  checkImpressions: () => {
    const { ads, user } = store.getState();
    const { lastImpression } = ads;
    const plan = User.getPlan(user.planId);

    if (!lastImpression) {
      return true;
    }

    const { advertisement: planAds } = plan || {};
    const impressions = planAds?.sections.impressions;
    if (impressions) {
      const offsetBetweenAds =
        (AdPeriodicity[impressions.periodicity] || AdPeriodicity['hour']) /
        (impressions.number || 1);

      const lastImpressionOffset = DateUtils.getOffset(
        lastImpression,
        Date.now()
      );

      return lastImpressionOffset.minutes >= offsetBetweenAds;
    }

    return false;
  },
  /**
   * Gets the ad source URL for a given section.
   * Validates GDPR consent, device capabilities, and impression timing.
   * Determines whether to use channel ads or plan ads based on configuration.
   *
   * @param {string} section - The section name (e.g., 'live', 'catchup', 'restart')
   * @param {boolean} [playback=false] - Whether this is for playback content
   * @param {Channel} [channel] - Optional channel object (defaults to current channel)
   * @returns {string} The parsed ad source URL, or empty string if ads should not play
   */
  getSource: (
    section: string,
    playback = false,
    channel?: Channel,
    avoidImpressions?: boolean,
    skipPreroll?: boolean,
    ignoreChannel?: boolean // This will force to only use plan url, instead of channel url
  ) => {
    let source = '';
    try {
      // if (!navigator.userActivation.hasBeenActive) {
      //   console.warn(
      //     '[Ad Utils] User has not interacted with the document yet'
      //   );
      //   return '';
      // }
      if (!CmpUtils.getGDPR()) {
        console.warn('[Ad Utils] No GDPR consent found or ads are disabled');
        return '';
      } else if (DeviceConfig?.capabilities?.disableAds?.default) {
        console.warn('[Ad Utils] Ads disabled on this device');
        return '';
      }
      const playAds = avoidImpressions ? true : AdUtils.checkImpressions();
      // const playAds = avoidImpressions ? true : false; // Use this to force adds

      if (!playAds) {
        console.warn(
          '[Ad Utils] Time offset between the current timestamp and the last impression is too short. Ads will not play.'
        );
        return '';
      }
      const { channels, user, router } = store.getState();
      const adsPlatform = DeviceConfig.info.adsPlatform;
      const plan = User.getPlan(user.planId);
      channel = channel || channels.currentChannel;

      const { advertisement: channelAds } = channel || {};
      const { advertisement: planAds } = plan || {};
      var adsEnabled =
        planAds?.enabled && planAds?.sections.vastTags[section]?.enabled; // If ads should be enabled based on channel and plan
      if (
        router.route.Page === Pages.ZAPPER ||
        router.route.Page === Pages.PLAYER
      ) {
        adsEnabled =
          adsEnabled &&
          channelAds?.enabled &&
          channelAds?.services?.vastTags[section]?.enabled;
      }
      // const channelAdsEnabled =
      //   channelAds?.services?.vastTags[section]?.enabled ?? false;
      // // const channelAdsEnabled = true; // Use this to force channel ads
      // const planAdsEnabled =
      //   planAds?.sections?.vastTags[section]?.enabled ?? false;
      // // const planAdsEnabled = true; // Use this to force ads in sections

      // If current playing channel is unavailable, do not play ads
      const channelUnavailable =
        plan &&
        section === 'live' &&
        (!channel?.plans.includes(plan?.id) || !adsEnabled);

      // If channel ads or plan ads are unavailable, do not play ads
      if (!adsEnabled || channelUnavailable) {
        return '';
      }

      // Else, obtain the ad source and parse it
      const channelSource = ignoreChannel
        ? ''
        : channelAds?.services?.vastTags[section]
        ? channelAds?.services?.vastTags[section][adsPlatform] ||
          channelAds?.services?.vastTags[section][adsPlatform.toLowerCase()] ||
          ''
        : '';
      const finalSource = channelSource?.length
        ? channelSource
        : planAds?.sections?.vastTags[section]
        ? planAds?.sections?.vastTags[section][adsPlatform] ||
          planAds?.sections?.vastTags[section][adsPlatform.toLowerCase()] ||
          ''
        : '';
      source = ParserUtils.sourceParser(finalSource || '');
      if (skipPreroll) {
        source = ParserUtils.prerollParser(source, section, playback);
      }
    } catch (error) {
      console.error('[Ad Utils] Get source:', error);
    }
    return source;
  },
  /**
   * Parses and updates the preroll count in an ad source URL.
   * Calculates the number of prerolls based on content type (FVOD/BVOD) and section.
   * Updates the 'pod_ad_slots' parameter in the URL.
   *
   * @param {string} source - The ad source URL containing the pod_ad_slots parameter
   * @param {string} [section] - The section name (e.g., 'catchup', 'recordings', 'startOver')
   * @param {boolean} [playback=false] - Whether this is for playback content
   * @returns {string} The URL with updated preroll count, or empty string if source is empty
   */
  parsePrerolls: (source: string, section?: string, playback = false) => {
    try {
      if (!source) return '';

      const { channels, user } = store.getState();
      const plan = User.getPlan(user.planId);

      const { advertisement: planAds } = plan || {};
      const { advertisement: channelAds, isFast } =
        channels.currentChannel || {};

      // Get number of prerolls

      let totalPrerolls = 1;
      if (playback) {
        // FVOD
        if (isFast) {
          const channelPrerolls = channelAds?.adsNumPrerolsFvod?.vod || 1;
          const planPrerolls = planAds?.sections?.adsNumPrerolsFvod?.vod || 1;
          totalPrerolls = Math.min(channelPrerolls, planPrerolls);
        }
        // BVOD
        else {
          let playbackSection = 'restart';
          switch (section) {
            case 'catchup':
            // @ts-ignore
            case 'events':
              playbackSection = 'u7d';
            // @ts-ignore
            case 'recordings':
              playbackSection = 'npvr';
            case 'startOver':
              const channelPrerolls =
                channelAds?.adsNumPrerolsBvod[playbackSection] || 1;
              const planPrerolls =
                planAds?.sections?.adsNumPrerolsBvod[playbackSection] || 1;
              totalPrerolls = Math.min(channelPrerolls, planPrerolls);
              break;
            default:
              break;
          }
        }
      }

      // Replace pod ad slots value with the number of prerolls

      const argument = 'pod_ad_slots=';
      const adSlotPosition = source.indexOf(argument) + argument?.length;
      const nextArgPosition = source.indexOf('&', adSlotPosition);
      source = `${source.substring(
        0,
        adSlotPosition
      )}${totalPrerolls}${source.substring(nextArgPosition, source?.length)}`;
    } catch (error) {
      console.error('[Ad Utils] Get prerolls:', error);
    }
    return source;
  },
  /**
   * Sends an image impression tracker by creating an invisible image element.
   * The image is automatically removed from the DOM after loading.
   *
   * @param {string} impression - The URL of the impression tracker to fire
   */
  sendImageImpression: (impression: string) => {
    if (typeof document !== 'undefined') {
      try {
        const img = document.createElement('img');
        img.setAttribute('src', impression);
        img.onload = () => {
          document.body.removeChild(img);
        };
        document.body.appendChild(img);
      } catch (error) {}
    }
  },
  /**
   * Fetches an ad image from a remote URL and parses the response.
   * Checks cache first to avoid redundant requests.
   * Parses the OpenRTB response to extract image URL and tracking information.
   *
   * @param {string | undefined} link - The URL to fetch the ad image from
   * @param {{ clicks: string[]; impressions: string[] } | undefined} trackers - Existing click and impression trackers
   * @returns {Promise<{ url: string | undefined; clickTrackers: string[]; impressionTrackers: string[] }>} Object containing the image URL and tracking arrays
   */
  fetchAdImage: async (
    link: string | undefined,
    trackers: { clicks: string[]; impressions: string[] } | undefined,
    adId: string
  ) => {
    const defaultReturn = {
      url: undefined as string | undefined,
      selectionTrackers: trackers?.clicks ?? [],
      impressionTrackers: trackers?.impressions ?? [],
      adId: adId,
    };

    if (!link) {
      return defaultReturn;
    }

    // CACHE: Uncomment this if caching is required
    // Check if we already have this ad image cached
    // const previousAdImage = AdImageHandlerInstance.getAdImage(link);
    // if (previousAdImage) {
    //   return {
    //     url: previousAdImage.url,
    //     selectionTrackers: previousAdImage.selectionTrackers,
    //     impressionTrackers: previousAdImage.focusTrackers,
    //   };
    // }

    try {
      const response = await fetch(link);
      if (!response.ok) {
        return defaultReturn;
      }

      const data = await response.json();
      const firstBid = data?.seatbid?.[0]?.bid?.[0];
      const firstAdId = data?.id;

      if (!firstBid?.adm) {
        return defaultReturn;
      }

      const adm = JSON.parse(firstBid.adm);
      const nativeAssets = adm?.native?.assets;

      if (!nativeAssets?.length || !nativeAssets[0]?.img?.url) {
        return defaultReturn;
      }

      const url = nativeAssets[0].img.url;
      // CACHE: Uncomment this if caching is required
      // Cache the ad image -> Not needed
      // AdImageHandlerInstance.addAdImage({
      //   request: link,
      //   url,
      //   date: Date.now(),
      //   selectionTrackers: adm.native?.link?.clicktrackers ?? [],
      //   focusTrackers: adm.native?.imptrackers ?? [],
      // });

      return {
        url,
        selectionTrackers: adm.native?.link?.clicktrackers ?? [],
        impressionTrackers: adm.native?.imptrackers ?? [],
        adId: firstAdId,
      };
    } catch (error) {
      return defaultReturn;
    }
  },
};

export default AdUtils;
